import React, { useState, useRef, useCallback } from 'react'
import { usePlanFeatures } from '../hooks/usePlanFeatures'

const EditableFloorMap = ({ 
  isOpen, 
  onClose, 
  targetRoom, 
  visitorInfo,
  isEditMode = false,
  onSaveMap = null
}) => {
  const { currentPlan, planFeatures, features } = usePlanFeatures()
  const [currentFloor, setCurrentFloor] = useState('1F')
  const [isEditing, setIsEditing] = useState(isEditMode)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [draggedRoom, setDraggedRoom] = useState(null)
  const mapRef = useRef(null)
  
  const safeTargetRoom = targetRoom || '会議室A'
  const safeVisitorInfo = visitorInfo || { name: 'ゲスト' }

  // 会議室の位置情報（編集可能）
  const [roomPositions, setRoomPositions] = useState({
    '会議室A': { floor: '1F', x: 100, y: 80, width: 70, height: 50, color: '#FF6B6B' },
    '会議室B': { floor: '1F', x: 280, y: 80, width: 70, height: 50, color: '#4ECDC4' },
    '会議室C': { floor: '2F', x: 100, y: 100, width: 70, height: 50, color: '#45B7D1' },
    '大会議室': { floor: '2F', x: 220, y: 80, width: 120, height: 80, color: '#96CEB4' },
    '応接室': { floor: '1F', x: 170, y: 180, width: 100, height: 60, color: '#FECA57' }
  })

  // 編集権限チェック（竹プラン以上）
  const canEdit = features.visualNavigation && (currentPlan === 'take' || currentPlan === 'matsu')

  const currentRoom = roomPositions[safeTargetRoom] || roomPositions['会議室A']
  
  React.useEffect(() => {
    if (currentRoom.floor) {
      setCurrentFloor(currentRoom.floor)
    }
  }, [currentRoom.floor])

  // 部屋の位置更新
  const updateRoomPosition = useCallback((roomName, newPosition) => {
    setRoomPositions(prev => ({
      ...prev,
      [roomName]: {
        ...prev[roomName],
        ...newPosition
      }
    }))
  }, [])

  // ドラッグ開始
  const handleDragStart = useCallback((e, roomName) => {
    if (!canEdit || !isEditing) return
    
    setDraggedRoom(roomName)
    setSelectedRoom(roomName)
    
    const rect = mapRef.current.getBoundingClientRect()
    const offsetX = e.clientX - rect.left - roomPositions[roomName].x
    const offsetY = e.clientY - rect.top - roomPositions[roomName].y
    
    e.dataTransfer.setData('text/plain', JSON.stringify({
      roomName,
      offsetX,
      offsetY
    }))
  }, [canEdit, isEditing, roomPositions])

  // ドラッグオーバー
  const handleDragOver = useCallback((e) => {
    if (!canEdit || !isEditing) return
    e.preventDefault()
  }, [canEdit, isEditing])

  // ドロップ
  const handleDrop = useCallback((e) => {
    if (!canEdit || !isEditing) return
    e.preventDefault()
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'))
      const rect = mapRef.current.getBoundingClientRect()
      
      const newX = Math.max(0, Math.min(
        400 - roomPositions[data.roomName].width,
        e.clientX - rect.left - data.offsetX
      ))
      const newY = Math.max(0, Math.min(
        300 - roomPositions[data.roomName].height,
        e.clientY - rect.top - data.offsetY
      ))

      updateRoomPosition(data.roomName, { x: newX, y: newY })
      setDraggedRoom(null)
    } catch (error) {
      console.error('Drop error:', error)
    }
  }, [canEdit, isEditing, roomPositions, updateRoomPosition])

  // 部屋のサイズ変更
  const handleRoomResize = useCallback((roomName, newSize) => {
    if (!canEdit || !isEditing) return
    updateRoomPosition(roomName, newSize)
  }, [canEdit, isEditing, updateRoomPosition])

  // 地図保存
  const handleSaveMap = useCallback(() => {
    if (onSaveMap) {
      onSaveMap(roomPositions)
    }
    
    // ローカルストレージに保存
    localStorage.setItem('alsok_floor_map', JSON.stringify(roomPositions))
    
    setIsEditing(false)
    alert('地図設定を保存しました')
  }, [roomPositions, onSaveMap])

  // 編集モード切替
  const toggleEditMode = useCallback(() => {
    if (!canEdit) {
      alert(`${planFeatures.name}では地図編集機能は利用できません`)
      return
    }
    setIsEditing(!isEditing)
    setSelectedRoom(null)
  }, [canEdit, isEditing, planFeatures.name])

  if (!isOpen) return null

  const isSecondFloor = currentFloor === '2F'
  const currentFloorRooms = Object.entries(roomPositions).filter(
    ([_, room]) => room.floor === currentFloor
  )

  return (
    <div className="modal-responsive">
      <div className="modal-content-responsive">
        {/* ヘッダー */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #f0f0f0',
          paddingBottom: '16px'
        }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              color: '#333',
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🗺️ フロアマップ
              {canEdit && isEditing && (
                <span style={{ fontSize: '14px', color: '#ff6b6b' }}>（編集モード）</span>
              )}
            </h2>
            <p style={{ 
              margin: '4px 0 0 0', 
              color: '#666',
              fontSize: '14px'
            }}>
              {safeVisitorInfo.name}様 → {safeTargetRoom}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999',
              padding: '4px'
            }}
          >
            ✕
          </button>
        </div>

        {/* 編集コントロール（竹プラン以上） */}
        {canEdit && (
          <div className="flex-responsive" style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <button
              onClick={toggleEditMode}
              style={{
                padding: '8px 16px',
                backgroundColor: isEditing ? '#ff6b6b' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {isEditing ? '📝 編集終了' : '✏️ 地図編集'}
            </button>
            
            {isEditing && (
              <button
                onClick={handleSaveMap}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                💾 保存
              </button>
            )}
            
            <span style={{ 
              fontSize: '12px', 
              color: '#666',
              marginLeft: 'auto'
            }}>
              {planFeatures.name} - 地図編集機能
            </span>
          </div>
        )}

        {/* フロア選択 */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '16px'
        }}>
          {['1F', '2F'].map(floor => (
            <button
              key={floor}
              onClick={() => setCurrentFloor(floor)}
              style={{
                padding: '8px 16px',
                backgroundColor: currentFloor === floor ? '#007bff' : '#e9ecef',
                color: currentFloor === floor ? 'white' : '#495057',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              {floor}
            </button>
          ))}
        </div>

        {/* 地図エリア */}
        <div
          ref={mapRef}
          style={{
            position: 'relative',
            width: '100%',
            height: '350px',
            backgroundColor: '#f8f9fa',
            border: '2px solid #dee2e6',
            borderRadius: '8px',
            margin: '16px 0',
            overflow: 'hidden',
            cursor: isEditing ? 'crosshair' : 'default'
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* グリッド（編集モード時） */}
          {isEditing && (
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            >
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ddd" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          )}

          {/* エレベーター・階段 */}
          <div style={{
            position: 'absolute',
            right: '20px',
            top: '20px',
            width: '40px',
            height: '40px',
            backgroundColor: '#6c757d',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px'
          }}>
            🛗
          </div>

          {/* 入口 */}
          <div style={{
            position: 'absolute',
            left: '50%',
            bottom: '10px',
            transform: 'translateX(-50%)',
            width: '60px',
            height: '20px',
            backgroundColor: '#28a745',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px'
          }}>
            🚪 入口
          </div>

          {/* 会議室 */}
          {currentFloorRooms.map(([roomName, room]) => (
            <div
              key={roomName}
              draggable={isEditing}
              onDragStart={(e) => handleDragStart(e, roomName)}
              onClick={() => isEditing && setSelectedRoom(roomName)}
              style={{
                position: 'absolute',
                left: `${room.x}px`,
                top: `${room.y}px`,
                width: `${room.width}px`,
                height: `${room.height}px`,
                backgroundColor: roomName === safeTargetRoom ? '#ff6b6b' : room.color,
                border: selectedRoom === roomName ? '3px solid #007bff' : `2px solid ${roomName === safeTargetRoom ? '#d63384' : '#fff'}`,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: isEditing ? 'move' : 'default',
                boxShadow: roomName === safeTargetRoom ? '0 0 10px rgba(255, 107, 107, 0.5)' : '0 2px 4px rgba(0,0,0,0.1)',
                opacity: draggedRoom === roomName ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              {roomName === safeTargetRoom && '👆'}
              <div style={{ textAlign: 'center', lineHeight: '1.2' }}>
                {roomName}
                {roomName === safeTargetRoom && (
                  <div style={{ fontSize: '10px', marginTop: '2px' }}>
                    目的地
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* 経路表示 */}
          {!isEditing && (
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#ff6b6b"
                  />
                </marker>
              </defs>
              <path
                d={`M 50 330 L 50 250 L ${currentRoom.x + currentRoom.width/2} 250 L ${currentRoom.x + currentRoom.width/2} ${currentRoom.y + currentRoom.height + 10}`}
                stroke="#ff6b6b"
                strokeWidth="3"
                fill="none"
                strokeDasharray="5,5"
                markerEnd="url(#arrowhead)"
              />
            </svg>
          )}
        </div>

        {/* 部屋情報（編集モード時） */}
        {isEditing && selectedRoom && (
          <div style={{
            padding: '12px',
            backgroundColor: '#e7f3ff',
            borderRadius: '8px',
            border: '1px solid #007bff'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#007bff' }}>
              {selectedRoom} の設定
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
              <div>X座標: {roomPositions[selectedRoom].x}px</div>
              <div>Y座標: {roomPositions[selectedRoom].y}px</div>
              <div>幅: {roomPositions[selectedRoom].width}px</div>
              <div>高さ: {roomPositions[selectedRoom].height}px</div>
            </div>
          </div>
        )}

        {/* 使用方法 */}
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#666'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            📍 現在地: 1F エントランス
          </div>
          {!isEditing ? (
            <div>
              赤い矢印に従って {safeTargetRoom} へお進みください。
              {canEdit && ' 地図をカスタマイズするには「地図編集」ボタンを押してください。'}
            </div>
          ) : (
            <div>
              <strong>編集方法:</strong><br/>
              • 会議室をドラッグして位置を変更<br/>
              • 会議室をクリックして詳細表示<br/>
              • 変更後は「保存」ボタンを押してください
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditableFloorMap